import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { ToastService } from '../services/toast.service';
import { UserService } from '../services/user.service';

interface ApiResponse {
    statusCode: number;
    [key: string]: unknown;
}

/**
 * Handles auth-related failures from the CropMap API envelope and from raw HTTP status.
 *
 * Some endpoints return HTTP 200 with `{ statusCode: 401 | 403 }` in the JSON body; others use
 * real 401/403 status codes. We inspect both paths so expired sessions are cleared consistently.
 *
 * 401 → {@link UserService.performAuthErrorReset} (same in-memory teardown as menu logout,
 * preserves rememberMe/language prefs, then full page reload so guards and resolvers re-run).
 * 403 → forbidden toast only; session stays active.
 */
export const AuthErrorInterceptor: HttpInterceptorFn = (req, next) => {
    const toastService = inject(ToastService);
    const userService = inject(UserService);

    const handle403 = () => {
        toastService.warn({ detail: 'cropmap.common.forbidden' });
    };

    const checkStatus = (statusCode: number) => {
        if (statusCode === 401) userService.performAuthErrorReset();
        else if (statusCode === 403) handle403();
    };

    return next(req).pipe(
        // Successful HTTP responses may still carry application-level 401/403 in the body.
        tap((event) => {
            if (!(event instanceof HttpResponse)) return;

            // Typical JSON API envelope (not file downloads).
            if (event.body && typeof event.body === 'object' && !(event.body instanceof Blob)) {
                const body = event.body as Record<string, unknown>;
                if ('statusCode' in body) {
                    checkStatus((body as unknown as ApiResponse).statusCode);
                }
                return;
            }

            // Blob responses (e.g. exports): parse text asynchronously to read statusCode if present.
            if (event.body instanceof Blob) {
                const reader = new FileReader();
                reader.onload = () => {
                    try {
                        const parsed = JSON.parse(reader.result as string) as Partial<ApiResponse>;
                        if (parsed?.statusCode) checkStatus(parsed.statusCode);
                    } catch { /* not JSON */ }
                };
                reader.readAsText(event.body);
            }
        }),
        // Transport-level 401/403 (no parsed body on the success path).
        catchError((error) => {
            if (error instanceof HttpErrorResponse) {
                checkStatus(error.status);
            }
            return throwError(() => error);
        }),
    );
};
